/**
 * HUGE♥HUG (VidyaHeart) indicator and entry rule.
 *
 * A port of the `f_trend()` block from the Pine strategy in
 * `components/ai-trading/Strategies/VidyaHeart_Strategy_Long.py`:
 *
 *   atr_value   = ta.atr(200)
 *   vidya_value = vidya_calc(src)
 *   upper_band  = vidya_value + atr_value * band_distance
 *   lower_band  = vidya_value - atr_value * band_distance
 *   is_trend_up := ta.crossover(src, upper_band)  ? true
 *                : ta.crossunder(src, lower_band) ? false : is_trend_up[1]
 *   smoothed_value = is_trend_up ? lower_band : upper_band
 *
 * The single plotted level — the heart — is the lower band while the trend is up and the
 * upper band while it is down, so it is green and below price in an uptrend and red and
 * above price in a downtrend. A close above the red heart turns it green, a close below the
 * green heart turns it red, and the colour is the standing direction: long while green,
 * short while red.
 *
 * Everything here works on closed candles with real high/low/close. The previous
 * implementation ran on sampled ticker prices with `high = low = close`, which reduces
 * true range to the absolute close-to-close change and makes `ta.atr` a different
 * quantity entirely, so the bands it produced did not correspond to the ones on the chart.
 */

export interface Candle {
  /** Bar open time in seconds. */
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface VidyaHeartParams {
  vidyaLength: number;
  vidyaMomentum: number;
  bandDistance: number;
  atrPeriod?: number;
}

export interface HeartState {
  /** The plotted level, or null before the trend has been established. */
  level: number | null;
  /** True while the heart is green (lower band), false while it is red (upper band). */
  isTrendUp: boolean | null;
  upper: number | null;
  lower: number | null;
}

/** Pine applies `ta.sma(vidya_value, 15)` before the bands are drawn. */
const VIDYA_SMOOTHING = 15;

/** `ta.atr(200)` in the Pine source. */
export const DEFAULT_ATR_PERIOD = 200;

/** `initialEntryLevel` values that map onto a Binance kline interval. */
export const HEART_INTERVALS = new Set(['1m', '5m', '15m', '1h', '1d', '1w', '1M']);

export const DEFAULT_HEART_INTERVAL = '1m';

export const DEFAULT_HEART_PARAMS: VidyaHeartParams = {
  vidyaLength: 14,
  vidyaMomentum: 20,
  bandDistance: 3.0
};

/**
 * Read the indicator settings out of a saved strategy config, falling back to the Pine
 * defaults for anything missing or unusable. Shared so that everything showing or trading
 * the heart derives it from the same numbers.
 */
export function heartParamsFromConfig(config: any): VidyaHeartParams {
  const positive = (value: any, fallback: number) =>
    Number(value) > 0 ? Number(value) : fallback;

  return {
    vidyaLength: positive(config?.vidyaLength, DEFAULT_HEART_PARAMS.vidyaLength),
    vidyaMomentum: positive(config?.vidyaMomentum, DEFAULT_HEART_PARAMS.vidyaMomentum),
    bandDistance: positive(config?.bandDistance, DEFAULT_HEART_PARAMS.bandDistance)
  };
}

/** The candle interval the heart is measured on, from `initialEntryLevel`. */
export function heartIntervalFromConfig(config: any): string {
  const level = config?.initialEntryLevel;
  return typeof level === 'string' && HEART_INTERVALS.has(level) ? level : DEFAULT_HEART_INTERVAL;
}

/**
 * Bars needed before the level can be trusted. The ATR dominates, and the VIDYA is a
 * recursive average seeded at zero, so it needs room to converge on top of that.
 */
export function requiredCandles(params: VidyaHeartParams): number {
  const atrPeriod = params.atrPeriod ?? DEFAULT_ATR_PERIOD;
  return atrPeriod + params.vidyaMomentum + VIDYA_SMOOTHING + 50;
}

/**
 * `vidya_calc` from the Pine source.
 *
 * Two details the earlier implementation got wrong and that change the result materially:
 * `ta.change(src)` is the one-bar difference, not the difference across the momentum
 * window, and `math.sum(..., vidya_momentum)` is a rolling sum over that window of those
 * one-bar changes.
 */
export function calculateVidya(
  closes: number[],
  length: number,
  momentum: number
): Array<number | null> {
  const count = closes.length;
  const result: Array<number | null> = new Array(count).fill(null);
  if (count === 0 || length < 1 || momentum < 1) return result;

  const changes: number[] = new Array(count).fill(0);
  for (let i = 1; i < count; i += 1) {
    changes[i] = closes[i] - closes[i - 1];
  }

  const alpha = 2 / (length + 1);

  // Pine declares `var float vidya_value = 0.0`, so the recursion starts from zero and
  // converges over the leading bars. That is why the warm-up above is generous.
  let vidyaValue = 0;
  const raw: number[] = new Array(count).fill(0);

  let sumPos = 0;
  let sumNeg = 0;

  for (let i = 0; i < count; i += 1) {
    const change = changes[i];
    if (change >= 0) sumPos += change;
    else sumNeg += -change;

    const leaving = i - momentum;
    if (leaving >= 0) {
      const old = changes[leaving];
      if (old >= 0) sumPos -= old;
      else sumNeg -= -old;
    }

    const total = sumPos + sumNeg;
    const absCmo = total > 0 ? Math.abs((100 * (sumPos - sumNeg)) / total) : 0;
    const adaptiveAlpha = (alpha * absCmo) / 100;

    vidyaValue = adaptiveAlpha * closes[i] + (1 - adaptiveAlpha) * vidyaValue;
    raw[i] = vidyaValue;
  }

  let window = 0;
  for (let i = 0; i < count; i += 1) {
    window += raw[i];
    if (i >= VIDYA_SMOOTHING) window -= raw[i - VIDYA_SMOOTHING];
    if (i >= VIDYA_SMOOTHING - 1) result[i] = window / VIDYA_SMOOTHING;
  }

  return result;
}

/**
 * `ta.atr(period)`: Wilder's running average of the true range, seeded with the simple
 * average of the first `period` ranges. True range needs the bar's high and low, which is
 * the reason this module takes candles rather than a price series.
 */
export function calculateAtr(candles: Candle[], period: number): Array<number | null> {
  const count = candles.length;
  const result: Array<number | null> = new Array(count).fill(null);
  if (count === 0 || period < 1) return result;

  const trueRanges: number[] = new Array(count).fill(0);
  trueRanges[0] = candles[0].high - candles[0].low;

  for (let i = 1; i < count; i += 1) {
    const { high, low } = candles[i];
    const prevClose = candles[i - 1].close;
    trueRanges[i] = Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose));
  }

  if (count < period) return result;

  let seed = 0;
  for (let i = 0; i < period; i += 1) seed += trueRanges[i];

  let rma = seed / period;
  result[period - 1] = rma;

  for (let i = period; i < count; i += 1) {
    rma = (rma * (period - 1) + trueRanges[i]) / period;
    result[i] = rma;
  }

  return result;
}

/**
 * The heart level and trend state on every bar.
 *
 * `is_trend_up` is a latch: it only changes when the close crosses a band, and holds its
 * value in between. It starts undefined and stays that way until the first crossing, which
 * is why `level` and `isTrendUp` are nullable rather than defaulted to a direction.
 */
export function computeHeartSeries(candles: Candle[], params: VidyaHeartParams): HeartState[] {
  const atrPeriod = params.atrPeriod ?? DEFAULT_ATR_PERIOD;
  const closes = candles.map(candle => candle.close);
  const vidya = calculateVidya(closes, params.vidyaLength, params.vidyaMomentum);
  const atr = calculateAtr(candles, atrPeriod);

  const series: HeartState[] = new Array(candles.length);

  let isTrendUp: boolean | null = null;
  let prevClose: number | null = null;
  let prevUpper: number | null = null;
  let prevLower: number | null = null;

  for (let i = 0; i < candles.length; i += 1) {
    const vidyaValue = vidya[i];
    const atrValue = atr[i];

    if (vidyaValue === null || atrValue === null) {
      series[i] = { level: null, isTrendUp: null, upper: null, lower: null };
      continue;
    }

    const upper = vidyaValue + atrValue * params.bandDistance;
    const lower = vidyaValue - atrValue * params.bandDistance;
    const close = closes[i];

    if (prevClose !== null && prevUpper !== null && prevClose <= prevUpper && close > upper) {
      isTrendUp = true;
    }
    if (prevClose !== null && prevLower !== null && prevClose >= prevLower && close < lower) {
      isTrendUp = false;
    }

    series[i] = {
      level: isTrendUp === null ? null : isTrendUp ? lower : upper,
      isTrendUp,
      upper,
      lower
    };

    prevClose = close;
    prevUpper = upper;
    prevLower = lower;
  }

  return series;
}

/**
 * The side the heart is currently asking for: long while it is green, short while it is red.
 *
 * This is a standing bias rather than a one-off event. Reading the latch's state instead of
 * its transitions is what lets the strategy re-enter in the same direction after a position
 * closes, for as long as the heart keeps its colour, rather than waiting for the next
 * crossing — which may be hours away.
 */
export function heartSide(
  state: { isTrendUp: boolean | null } | undefined | null
): 'buy' | 'sell' | null {
  if (!state || state.isTrendUp === null) return null;
  return state.isTrendUp ? 'buy' : 'sell';
}

/** The fewest consecutive same-colour bars that can precede the one that turns. */
const MIN_REVERSAL_RUN = 3;

/** A price the next bar has to trade through before the strategy will enter. */
export interface ReversalSetup {
  side: 'buy' | 'sell';
  /** Crossed upwards for a buy, downwards for a sell. */
  level: number;
  /** Bar that produced the setup, so it can be discarded once superseded. */
  barTime: number;
  /** How many same-colour bars the turn reversed: three at least, often more. */
  runLength: number;
}

const isRed = (candle: Candle) => candle.close < candle.open;
const isGreen = (candle: Candle) => candle.close > candle.open;

/**
 * How many bars immediately before `endExclusive`, counting backwards, satisfy `matches`.
 *
 * A bar that is neither red nor green — one that closed exactly where it opened — ends the
 * run, because a doji is not the colour the pattern is asking for.
 */
function runLengthBefore(
  candles: Candle[],
  endExclusive: number,
  matches: (candle: Candle) => boolean
): number {
  let length = 0;
  for (let index = endExclusive - 1; index >= 0 && matches(candles[index]); index--) {
    length++;
  }
  return length;
}

/**
 * A reversal on the bar that just closed, used as a second gate on top of the heart's colour
 * so re-entries happen on a turn rather than anywhere inside a trend.
 *
 * Long: at least three consecutive red bars, then this one closing green. Short is the mirror
 * — three or more green, then one red. The run is measured rather than assumed, so a turn off
 * seven red bars counts the same as one off three; the minimum is a floor, not a requirement
 * that the run be exactly that long.
 *
 * The pattern only arms the trade; it does not open it. The level returned is the turning
 * candle's own high (long) or low (short), and price has to trade through it *after* that
 * candle has closed. So the entry is a stop above the green candle rather than a fill at
 * its close, and a setup that never gets taken out never becomes a position.
 *
 * @returns the armed setup, or null if the bar that just closed is not a turn
 */
export function detectReversalSetup(candles: Candle[]): ReversalSetup | null {
  if (candles.length < MIN_REVERSAL_RUN + 1) return null;

  const last = candles.length - 1;
  const current = candles[last];

  if (isGreen(current)) {
    const runLength = runLengthBefore(candles, last, isRed);
    if (runLength >= MIN_REVERSAL_RUN) {
      return { side: 'buy', level: current.high, barTime: current.time, runLength };
    }
  }

  if (isRed(current)) {
    const runLength = runLengthBefore(candles, last, isGreen);
    if (runLength >= MIN_REVERSAL_RUN) {
      return { side: 'sell', level: current.low, barTime: current.time, runLength };
    }
  }

  return null;
}

/** Whether a live price has taken out the level an armed setup is waiting on. */
export function setupTriggered(setup: ReversalSetup, price: number): boolean {
  return setup.side === 'buy' ? price > setup.level : price < setup.level;
}
